'use client';

import { useState, useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';

type Props = {
  postSlug?: string;
};

export default function ReadingProgress({ postSlug }: Props) {
  const [progress, setProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState('');
  const finishFiredRef = useRef(false);
  const viewFiredRef = useRef(false);
  const startedAtRef = useRef(Date.now());
  const maxDepthRef = useRef(0);

  useEffect(() => {
    if (postSlug && !viewFiredRef.current) {
      viewFiredRef.current = true;
      track('blog_post_viewed', { slug: postSlug });
    }
  }, [postSlug]);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const headings = Array.from(document.querySelectorAll<HTMLElement>('.fp-article-page .prose h2[id]'));
      const readingLine = scrollTop + Math.min(180, window.innerHeight * 0.28);
      const nearest = headings.reduce<HTMLElement | null>(
        (current, heading) => (
          heading.getBoundingClientRect().top + scrollTop <= readingLine ? heading : current
        ),
        null,
      ) || headings[0];
      setCurrentSection(nearest?.textContent?.trim() || '');

      if (docHeight > 0) {
        const pct = Math.min(100, (scrollTop / docHeight) * 100);
        setProgress(pct);
        maxDepthRef.current = Math.max(maxDepthRef.current, pct);

        if (postSlug && pct >= 90 && !finishFiredRef.current) {
          finishFiredRef.current = true;
          track('blog_post_completed', {
            slug: postSlug,
            active_reading_time_seconds: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
            max_depth: Math.round(maxDepthRef.current),
          });
        }
      }
    };

    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    updateProgress();
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, [postSlug]);

  const openIndex = () => {
    const index = document.querySelector<HTMLDetailsElement>('#article-index');
    if (!index) return;
    index.open = true;
    index.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    window.setTimeout(() => index.querySelector<HTMLElement>('summary')?.focus(), 250);
  };

  return <>
    <div
      className="fp-reading-progress fixed top-0 left-0 h-[3px] bg-primary z-[60] transition-[width] duration-100"
      style={{ width: `${progress}%` }}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Reading progress"
    />
    {progress > 8 && (
      <button
        className="fp-reading-return"
        type="button"
        onClick={openIndex}
        aria-label={`Open article index for ${currentSection || 'current section'}`}
      >
        <span>{currentSection || 'Article sections'}</span>
        <strong aria-hidden="true">↑</strong>
      </button>
    )}
  </>;
}
